import type { CSSProperties } from 'react';
import { type GameTypeId, type ThemeConfig, WHEEL_ACCENT_FALLBACK } from '../lib/types';

/**
 * A faithful, theme-aware mini preview of each storefront game. Mirrors how the
 * theme extension renders so merchants see the actual game (spin wheel, scratch
 * card, lucky box, gift/mystery) styled with their theme — not a generic wheel.
 */
export function GamePreview({
  gameType,
  config,
  segmentLabels = ['10% OFF', 'Free ship', 'Try again', '5% OFF', 'Mystery', 'No win'],
}: {
  gameType: GameTypeId;
  config: ThemeConfig;
  segmentLabels?: string[];
}): JSX.Element {
  const { colors, borderRadius, fontFamily } = config;

  const shell: CSSProperties = {
    background: colors.background,
    color: colors.text,
    borderRadius: `${borderRadius}px`,
    fontFamily,
    padding: 28,
    textAlign: 'center',
    boxShadow: '0 18px 50px rgba(0,0,0,0.18)',
    position: 'relative',
    overflow: 'hidden',
  };

  const button: CSSProperties = {
    border: 0,
    borderRadius: 9999,
    padding: '11px 28px',
    fontWeight: 700,
    fontSize: 15,
    color: colors.buttonText,
    background: colors.button,
    cursor: 'pointer',
    boxShadow: `0 6px 18px ${hexA(colors.button, 0.35)}`,
  };

  return (
    <div style={shell}>
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(120% 80% at 50% -10%, ${hexA(colors.primary, 0.16)} 0%, transparent 60%)`,
          pointerEvents: 'none',
        }}
      />
      <div style={{ position: 'relative' }}>
        <div style={{ fontWeight: 800, fontSize: 20, marginBottom: 4 }}>You&apos;ve unlocked a prize!</div>
        <div style={{ opacity: 0.7, fontSize: 13, marginBottom: 18 }}>
          {labelFor(gameType)} — play to reveal your reward
        </div>
        <GameVisual gameType={gameType} config={config} segmentLabels={segmentLabels} />
        <button type="button" style={button}>
          {ctaFor(gameType)}
        </button>
      </div>
    </div>
  );
}

function GameVisual({
  gameType,
  config,
  segmentLabels,
}: {
  gameType: GameTypeId;
  config: ThemeConfig;
  segmentLabels: string[];
}): JSX.Element {
  const { colors } = config;
  const wrap: CSSProperties = { margin: '0 auto 18px' };

  if (gameType === 'spin_wheel') {
    // Mirrors the storefront board: studded frame, metallic hub and a pointer
    // rising from the centre. Kept in step with `.pf-wheel-*` in playforge.css —
    // if the preview drifts, merchants customise against a wheel they won't get.
    const SIZE = 190;
    const n = Math.max(6, Math.min(8, segmentLabels.length));
    const slice = 360 / n;
    const stops = Array.from({ length: n }, (_, i) => {
      const c = i % 2 === 0 ? colors.primary : hexA(colors.primary, 0.55);
      return `${c} ${i * slice}deg ${(i + 1) * slice}deg`;
    }).join(', ');
    const bezel = colors.wheelBezel || colors.primary;
    const gold = metallicShades(colors.wheelAccent || WHEEL_ACCENT_FALLBACK);
    const studs = Array.from({ length: 16 }, (_, i) => {
      const a = (i / 16) * 2 * Math.PI;
      return { left: 50 + 46.5 * Math.cos(a), top: 50 + 46.5 * Math.sin(a) };
    });

    return (
      <div style={{ ...wrap, position: 'relative', width: SIZE, height: SIZE }}>
        {/* Frame */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            backgroundColor: bezel,
            backgroundImage:
              'radial-gradient(circle at 50% 38%, rgba(255,255,255,0.22), rgba(0,0,0,0.28) 72%)',
            boxShadow:
              '0 10px 24px rgba(0,0,0,0.32), inset 0 5px 10px rgba(255,255,255,0.22), inset 0 -6px 12px rgba(0,0,0,0.35)',
          }}
        />
        {/* Studs */}
        {studs.map((s, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: `${s.left}%`,
              top: `${s.top}%`,
              width: 6,
              height: 6,
              margin: -3,
              borderRadius: '50%',
              background: `radial-gradient(circle at 35% 30%, ${gold.light}, ${gold.dark})`,
              boxShadow: '0 1px 2px rgba(0,0,0,0.5)',
              zIndex: 4,
            }}
          />
        ))}
        {/* Disc */}
        <div
          style={{
            position: 'absolute',
            inset: '7%',
            borderRadius: '50%',
            background: `conic-gradient(${stops})`,
            boxShadow: 'inset 0 0 0 4px rgba(255,255,255,0.9), inset 0 0 14px rgba(0,0,0,0.25)',
            zIndex: 2,
          }}
        />
        {/* Hub */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '22%',
            height: '22%',
            transform: 'translate(-50%,-50%)',
            borderRadius: '50%',
            background: `radial-gradient(circle at 38% 32%, ${gold.light}, ${gold.mid} 55%, ${gold.dark})`,
            border: '2px solid rgba(255,255,255,0.35)',
            boxShadow:
              '0 3px 8px rgba(0,0,0,0.45), inset 0 -2px 5px rgba(0,0,0,0.4), inset 0 2px 5px rgba(255,255,255,0.5)',
            zIndex: 5,
          }}
        />
        {/* Pointer rising from the hub to 12 o'clock */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: 0,
            height: 0,
            transform: 'translate(-50%,-100%)',
            marginTop: '-9%',
            borderLeft: '10px solid transparent',
            borderRight: '10px solid transparent',
            borderBottom: `28px solid ${gold.light}`,
            filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.45))',
            zIndex: 6,
          }}
        />
      </div>
    );
  }

  if (gameType === 'scratch_card') {
    return (
      <div
        style={{
          ...wrap,
          position: 'relative',
          width: 220,
          height: 130,
          borderRadius: 14,
          overflow: 'hidden',
          background: hexA(colors.primary, 0.18),
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            fontSize: 22,
            color: colors.text,
          }}
        >
          🎁 {segmentLabels[0] ?? 'PRIZE'}
        </div>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            clipPath: 'polygon(0 0, 100% 0, 100% 100%, 38% 100%, 30% 60%, 0 72%)',
            background:
              'repeating-linear-gradient(45deg, #c0c4cc, #c0c4cc 10px, #d9dce2 10px, #d9dce2 20px)',
            color: '#6b7280',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
          }}
        >
          Scratch here
        </div>
      </div>
    );
  }

  if (gameType === 'lucky_box') {
    return (
      <div style={{ ...wrap, display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, width: 220 }}>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            style={{
              aspectRatio: '1',
              borderRadius: 12,
              fontSize: 26,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: i === 4 ? colors.primary : hexA(colors.primary, 0.15),
            }}
          >
            🎁
          </div>
        ))}
      </div>
    );
  }

  // gift_box / mystery_gift — a single reveal cover
  return (
    <div
      style={{
        ...wrap,
        width: 180,
        height: 130,
        borderRadius: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 54,
        color: colors.buttonText,
        background: `linear-gradient(135deg, ${colors.primary}, ${colors.button})`,
        boxShadow: `0 10px 26px ${hexA(colors.primary, 0.4)}`,
      }}
    >
      {gameType === 'mystery_gift' ? '❓' : '🎁'}
    </div>
  );
}

function labelFor(t: GameTypeId): string {
  return GAME_LABELS[t] ?? 'Game';
}
function ctaFor(t: GameTypeId): string {
  switch (t) {
    case 'spin_wheel':
      return 'Spin to win';
    case 'scratch_card':
      return 'Scratch & win';
    case 'lucky_box':
      return 'Pick a box';
    default:
      return 'Open gift';
  }
}

export const GAME_LABELS: Record<GameTypeId, string> = {
  spin_wheel: 'Spin Wheel',
  scratch_card: 'Scratch Card',
  lucky_box: 'Lucky Box',
  gift_box: 'Gift Box',
  mystery_gift: 'Mystery Gift',
};

export const GAME_EMOJI: Record<GameTypeId, string> = {
  spin_wheel: '🎡',
  scratch_card: '🎟️',
  lucky_box: '🎁',
  gift_box: '📦',
  mystery_gift: '❓',
};

/** Expand a #rrggbb hex to an rgba() string with the given alpha. */
/**
 * Highlight / body / shadow shades of a metallic accent from one colour.
 * Mirrors `metallicShades` in the theme extension so the preview and the
 * storefront derive identical golds from the merchant's single picked colour.
 */
function metallicShades(hex: string): { light: string; mid: string; dark: string } {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  if (!m) return { light: '#f6d365', mid: '#c8951a', dark: '#8a5f0d' };
  const rgb = [parseInt(m[1]!, 16), parseInt(m[2]!, 16), parseInt(m[3]!, 16)];
  const towards = (target: number, amount: number): string =>
    `rgb(${rgb.map((c) => Math.round(c + (target - c) * amount)).join(', ')})`;
  return { light: towards(255, 0.35), mid: towards(0, 0), dark: towards(0, 0.42) };
}

function hexA(hex: string, alpha: number): string {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  if (!m) return hex;
  const r = parseInt(m[1]!, 16);
  const g = parseInt(m[2]!, 16);
  const b = parseInt(m[3]!, 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
